import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import { ArrowLeftRight, Coins, Flame, ShieldAlert, UserSearch } from 'lucide-react'
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'

import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { Page, PageHeader, StatCard } from '@/components/shell'
import { chartTickFont, useChartTheme } from '@/lib/chart-theme'
import { api, type DailyPoint, type Overview, type TokenRow, type TopToken } from '@/lib/api'
import { formatUnits, shortAddress } from '@/lib/format'
import { useMe } from '@/lib/session'

export const Route = createFileRoute('/dashboard/')({
  component: OverviewPage,
})

function OverviewPage() {
  const me = useMe()

  // recharts 的颜色是 SVG presentation attribute，var() 在那里不解析，
  // 所以图表配色只能按当前主题取一套字面量（见 lib/chart-theme.ts）。
  const {
    colors: chartColors,
    areaFill,
    tooltipStyle: chartTooltipStyle,
    tooltipLabelStyle: chartTooltipLabelStyle,
  } = useChartTheme()

  const { data: stats } = useQuery({
    queryKey: ['stats', 'overview'],
    queryFn: () => api.get<Overview>('/stats/overview'),
  })
  const { data: daily } = useQuery({
    queryKey: ['stats', 'daily'],
    queryFn: () => api.get<{ data: DailyPoint[] }>('/stats/daily', { days: 30 }),
  })
  const { data: top } = useQuery({
    queryKey: ['stats', 'top-tokens'],
    queryFn: () => api.get<{ data: TopToken[] }>('/stats/top-tokens', { limit: 6 }),
  })
  const { data: hot } = useQuery({
    queryKey: ['tokens', 'hot'],
    queryFn: () => api.get<{ data: TokenRow[] }>('/tokens/hot'),
  })

  return (
    <Page>
      <PageHeader
        title="概览"
        description={`你好，${me?.user.name ?? ''}。以下是 Launchpad 当前的运营态势。`}
      />

      <div className="stagger grid gap-3.5 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          icon={Coins}
          label="代币总数"
          value={stats?.tokens.total ?? '—'}
          hint={
            stats ? `交易中 ${stats.tokens.live} · 已毕业 ${stats.tokens.graduated}` : undefined
          }
        />
        <StatCard
          icon={ArrowLeftRight}
          label="成交笔数"
          value={stats?.trades.total.toLocaleString('en-US') ?? '—'}
          hint={stats ? `近 7 天 ${stats.trades.last7d} 笔` : undefined}
          tone="cyan"
        />
        <StatCard
          icon={UserSearch}
          label="参与地址"
          value={stats?.actors.traders ?? '—'}
          hint={stats ? `发行人 ${stats.actors.creators} 个` : undefined}
        />
        <StatCard
          icon={ShieldAlert}
          label="已下架"
          value={stats?.tokens.delisted ?? '—'}
          hint={stats ? `热门标记 ${stats.tokens.hot} 个` : undefined}
          tone={stats && stats.tokens.delisted > 0 ? 'warn' : 'default'}
        />
      </div>

      <div className="surface p-6">
        <div className="mb-5 flex items-baseline justify-between">
          <div>
            <h2 className="text-[15px] font-medium tracking-tight text-txt">近 30 天活动</h2>
            <p className="mt-1 text-[12px] text-mut2">每日成交笔数与新发币数</p>
          </div>
          <div className="flex items-center gap-4 text-[11.5px]">
            <Legend color={chartColors.cyan} label="成交" />
            <Legend color={chartColors.rose} label="发币" />
          </div>
        </div>

        {!daily ? (
          <Skeleton className="h-[260px] w-full rounded-apple" />
        ) : (
          <ResponsiveContainer width="100%" height={260}>
            {/* left 不要收成负值：Y 轴刻度过千后是 4 位数，负边距会把最高一档的
                首位裁掉（显示成 "000"）。宽度按 4 位数留足。 */}
            <AreaChart data={daily.data} margin={{ top: 4, right: 4, bottom: 0, left: 0 }}>
              {/* 填充浓度分主题，取值与理由见 chart-theme.ts 的 AREA_FILL */}
              <defs>
                <linearGradient id="gTrades" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={chartColors.cyan} stopOpacity={areaFill.primary} />
                  <stop offset="100%" stopColor={chartColors.cyan} stopOpacity={0} />
                </linearGradient>
                <linearGradient id="gLaunches" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={chartColors.rose} stopOpacity={areaFill.secondary} />
                  <stop offset="100%" stopColor={chartColors.rose} stopOpacity={0} />
                </linearGradient>
              </defs>

              {/* 网格极淡、只留横线——Apple 图表几乎不画竖向网格 */}
              <CartesianGrid stroke={chartColors.grid} vertical={false} />
              <XAxis
                dataKey="day"
                tickLine={false}
                axisLine={false}
                tick={{ fill: chartColors.tick, fontSize: 11, fontFamily: chartTickFont }}
                tickFormatter={(d: string) => d.slice(5)}
                minTickGap={28}
              />
              <YAxis
                tickLine={false}
                axisLine={false}
                tick={{ fill: chartColors.tick, fontSize: 11, fontFamily: chartTickFont }}
                width={48}
              />
              <Tooltip
                cursor={{ stroke: chartColors.cursor }}
                contentStyle={chartTooltipStyle}
                labelStyle={chartTooltipLabelStyle}
              />
              <Area
                type="monotone"
                dataKey="trades"
                name="成交"
                stroke={chartColors.cyan}
                strokeWidth={2}
                fill="url(#gTrades)"
              />
              <Area
                type="monotone"
                dataKey="launches"
                name="发币"
                stroke={chartColors.rose}
                strokeWidth={2}
                fill="url(#gLaunches)"
              />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>

      <div className="grid gap-3.5 lg:grid-cols-2">
        <div className="surface p-6">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-[15px] font-medium tracking-tight text-txt">分计价币成交额榜</h2>
            <Link to="/dashboard/tokens" className="text-[12px] text-cyan hover:underline">
              全部代币
            </Link>
          </div>

          {!top ? (
            <ListSkeleton />
          ) : top.data.length === 0 ? (
            <p className="py-6 text-center text-[13px] text-mut2">暂无成交数据</p>
          ) : (
            <ul className="-mx-2">
              {top.data.slice(0, 8).map((t) => (
                <li key={t.tokenAddress}>
                  <Link
                    to="/dashboard/tokens/$address"
                    params={{ address: t.tokenAddress }}
                    className="row-hover flex items-center gap-3 rounded-apple-sm px-2 py-2.5"
                  >
                    <span className="tnum w-4 text-center font-mono text-[11px] text-dim">
                      {t.quoteRank}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5">
                        <span className="text-[13.5px] text-txt">{t.symbol}</span>
                        {t.hidden && (
                          <Badge className="h-4 bg-warn/15 px-1.5 text-[10px] text-warn hover:bg-warn/15">
                            已下架
                          </Badge>
                        )}
                      </div>
                      <div className="truncate text-[11.5px] text-mut2">{t.name}</div>
                    </div>
                    <div className="text-right">
                      <div className="tnum font-mono text-[12.5px] text-txt">
                        {formatUnits(t.volume, t.quoteDecimals, 2)} {t.quoteSymbol ?? '未知'}
                      </div>
                      <div className="tnum font-mono text-[10.5px] text-mut2">
                        {t.tradeCount} 笔
                      </div>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="surface p-6">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="flex items-center gap-1.5 text-[15px] font-medium tracking-tight text-txt">
              <Flame className="size-[15px] text-rose" />
              当前热门
            </h2>
            <span className="font-mono text-[11px] text-mut2">
              {hot ? `${hot.data.length} 个生效中` : ''}
            </span>
          </div>

          {!hot ? (
            <ListSkeleton />
          ) : hot.data.length === 0 ? (
            <p className="py-6 text-center text-[13px] text-mut2">当前没有生效中的热门标记</p>
          ) : (
            <ul className="-mx-2">
              {hot.data.slice(0, 6).map((t) => (
                <li key={t.tokenAddress}>
                  <Link
                    to="/dashboard/tokens/$address"
                    params={{ address: t.tokenAddress }}
                    className="row-hover flex items-center justify-between gap-3 rounded-apple-sm px-2 py-2.5"
                  >
                    <div className="min-w-0">
                      <div className="text-[13.5px] text-txt">{t.symbol}</div>
                      <div className="truncate text-[11.5px] text-mut2">
                        {shortAddress(t.tokenAddress)}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="tnum font-mono text-[11.5px] text-rose">
                        权重 {t.hotWeight}
                      </div>
                      <div className="font-mono text-[10.5px] text-mut2">
                        {t.hotExpiresAt
                          ? `到期 ${new Date(t.hotExpiresAt).toLocaleDateString('zh-CN')}`
                          : '长期有效'}
                      </div>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </Page>
  )
}

function Legend({ color, label }: { color: string; label: string }) {
  return (
    <span className="flex items-center gap-1.5 text-mut2">
      <span className="size-2 rounded-full" style={{ backgroundColor: color }} />
      {label}
    </span>
  )
}

function ListSkeleton() {
  return (
    <div className="space-y-2.5">
      {Array.from({ length: 5 }, (_, i) => (
        <Skeleton key={i} className="h-11 w-full rounded-apple-sm" />
      ))}
    </div>
  )
}
